import { useGame, usePlayer } from "@empirica/core/player/classic/react";
import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import { Button } from "../components/Button";

const censusRaceOptions = [
  "White",
  "Black or African American",
  "American Indian or Alaska Native",
  "Asian",
  "Native Hawaiian or Other Pacific Islander",
  "Some other race",
];

const llmUsageCategories = [
  { key: "writing", label: "Writing (drafting, editing, summarizing)" },
  { key: "practical_guidance", label: "Practical guidance (how-to advice, tutoring, brainstorming)" },
  { key: "search_substitute", label: "Search substitute (looking up facts, products, recipes)" },
  { key: "social_interaction", label: "Social interaction (discussing emotions, relationships, or personal reflection)" },
];

const llmUsageKeys = llmUsageCategories.map((cat) => cat.key);
const llmUsageFrequencyOptions = [
  { value: "never", label: "Never" },
  { value: "less_than_monthly", label: "Less than monthly" },
  { value: "monthly", label: "Monthly" },
  { value: "weekly", label: "Weekly" },
  { value: "daily", label: "Daily" },
  { value: "constantly", label: "Constantly" },
];
const llmOptions = ["ChatGPT", "Claude", "Gemini", "Grok", "Kimi"];
// Source for warmth/competence question battery: doi/10.1177/21582440251345896
const aiChatbotImpressionItems = [
  { key: "friendly", dimension: "warmth", label: "friendly" },
  { key: "good_intentions", dimension: "warmth", label: "Good intentioned" },
  { key: "trustworthy", dimension: "warmth", label: "trustworthy" },
  { key: "compassionate", dimension: "warmth", label: "compassionate" },
  { key: "gentle", dimension: "warmth", label: "gentle" },
  { key: "truthful", dimension: "competence", label: "truthful" },
  { key: "competent", dimension: "competence", label: "competent" },
  { key: "efficient", dimension: "competence", label: "efficient" },
  { key: "professional", dimension: "competence", label: "professional" },
  { key: "effective", dimension: "competence", label: "effective" },
  { key: "intelligent", dimension: "competence", label: "intelligent" },
  { key: "skillful", dimension: "competence", label: "skillful" },
];
const aiChatbotImpressionKeys = aiChatbotImpressionItems.map((item) => item.key);
const aiChatbotLikertOptions = [
  { value: "1", label: "Strongly\ndisagree" },
  { value: "2", label: "Disagree" },
  { value: "3", label: "Somewhat\ndisagree" },
  { value: "4", label: "Neither agree\nnor disagree" },
  { value: "5", label: "Somewhat\nagree" },
  { value: "6", label: "Agree" },
  { value: "7", label: "Strongly\nagree" },
];

function capitalizeLabel(value) {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function normalizeLlmUsageFrequency(value) {
  const valueMap = {
    1: "never",
    2: "less_than_monthly",
    3: "monthly",
    4: "weekly",
    5: "daily",
  };

  if (valueMap[value]) {
    return valueMap[value];
  }

  return llmUsageFrequencyOptions.some((option) => option.value === value)
    ? value
    : "";
}

function shuffleArray(values) {
  const shuffled = [...values];

  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(Math.random() * (index + 1));
    [shuffled[index], shuffled[swapIndex]] = [shuffled[swapIndex], shuffled[index]];
  }

  return shuffled;
}

function getInitialAiChatbotImpressionOrder(existingOrder) {
  if (
    Array.isArray(existingOrder) &&
    existingOrder.length === aiChatbotImpressionKeys.length &&
    aiChatbotImpressionKeys.every((key) => existingOrder.includes(key))
  ) {
    return existingOrder;
  }

  return shuffleArray(aiChatbotImpressionKeys);
}

export function IntroSurvey({ next }) {
  const game = useGame();
  const player = usePlayer();
  const pageTopRef = useRef(null);
  const [surveyPage, setSurveyPage] = useState(1);
  const treatment = game?.get("treatment") || player.get("queueTreatment") || {};
  const design = game?.get("design") || {};
  const testMode = Boolean((treatment.testMode ?? design.testMode) ?? false);

  const existingDemographics = player.get("demographics") || {};
  const existingRaceEthnicity = existingDemographics.raceEthnicity || {};
  const existingLlmUsageFrequency = player.get("llmUsageFrequency") || {};
  const existingAiChatbotImpressions = player.get("aiChatbotImpressions") || {};
  const initialPrePetCatsOnRight = player.get("prePetCatsOnRight");
  const catsOnRight = useMemo(
    () =>
      typeof initialPrePetCatsOnRight === "boolean"
        ? initialPrePetCatsOnRight
        : Math.random() < 0.5,
    [initialPrePetCatsOnRight]
  );
  const leftPetLabel = catsOnRight ? "Dogs" : "Cats";
  const rightPetLabel = catsOnRight ? "Cats" : "Dogs";

  const [age, setAge] = useState(existingDemographics.age || "");
  const [education, setEducation] = useState(existingDemographics.education || "");
  const [gender, setGender] = useState(existingDemographics.gender || "");
  const [hispanicLatinoOrigin, setHispanicLatinoOrigin] = useState(
    existingRaceEthnicity.hispanicLatinoOrigin || ""
  );
  const [raceSelections, setRaceSelections] = useState(
    Array.isArray(existingRaceEthnicity.races) ? existingRaceEthnicity.races : []
  );
  const [prePetSliderValue, setPrePetSliderValue] = useState(
    player.get("prePetSliderValue") ?? 50
  );
  const [prePetSliderTouched, setPrePetSliderTouched] = useState(
    player.get("prePetSliderValue") != null
  );
  const [generalLlmUsage, setGeneralLlmUsage] = useState(
    normalizeLlmUsageFrequency(player.get("generalLlmUsage"))
  );
  const [llmUsage, setLlmUsage] = useState(() =>
    Object.fromEntries(
      llmUsageKeys.map((key) => [
        key,
        normalizeLlmUsageFrequency(existingLlmUsageFrequency[key]),
      ])
    )
  );
  const [llmHeardOf, setLlmHeardOf] = useState(() => {
    const existing = player.get("llmHeardOf");
    return Array.isArray(existing) ? existing : [];
  });
  const [llmUsed, setLlmUsed] = useState(() => {
    const existing = player.get("llmUsed");
    return Array.isArray(existing) ? existing : [];
  });
  const [aiChatbotImpressions, setAiChatbotImpressions] = useState(() =>
    Object.fromEntries(
      aiChatbotImpressionKeys.map((key) => [
        key,
        existingAiChatbotImpressions[key] || "",
      ])
    )
  );
  const [aiChatbotImpressionOrder] = useState(() =>
    getInitialAiChatbotImpressionOrder(player.get("aiChatbotImpressionItemOrder"))
  );

  const labelClassName = "block text-2xl font-medium text-gray-700 my-2";
  const surveyQuestionClassName = "block text-2xl font-semibold text-gray-800";
  const surveyOptionClassName = "flex items-center gap-2 text-lg text-gray-700";
  const selectionInputClassName =
    "appearance-none block w-full px-3 py-2 text-lg border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-empirica-500 focus:border-empirica-500";

  const hasDemographics =
    age !== "" &&
    education !== "" &&
    gender !== "" &&
    hispanicLatinoOrigin !== "" &&
    raceSelections.length > 0;
  const hasLlmUsage =
    generalLlmUsage !== "" &&
    llmUsageKeys.every((key) => llmUsage[key] !== "");
  const hasAiChatbotImpressions = aiChatbotImpressionKeys.every(
    (key) => aiChatbotImpressions[key] !== ""
  );

  useLayoutEffect(() => {
    pageTopRef.current?.scrollIntoView({ block: "start", inline: "nearest" });
  }, [surveyPage]);

  function handleRaceSelectionChange(race, isChecked) {
    setRaceSelections((current) => {
      if (isChecked) {
        if (current.includes(race)) {
          return current;
        }
        return [...current, race];
      }

      return current.filter((selectedRace) => selectedRace !== race);
    });
  }

  function handleLlmUsageChange(key, value) {
    setLlmUsage((current) => ({
      ...current,
      [key]: value,
    }));
  }

  function handleAiChatbotImpressionChange(key, value) {
    setAiChatbotImpressions((current) => ({
      ...current,
      [key]: value,
    }));
  }

  function handlePrePetSliderChange(event) {
    setPrePetSliderValue(Number(event.target.value));
    setPrePetSliderTouched(true);
  }

  function handleSubmit(event) {
    event.preventDefault();
    player.set("demographics", {
      age,
      education,
      gender,
      raceEthnicity: {
        hispanicLatinoOrigin,
        races: raceSelections,
      },
    });
    player.set("llmUsageFrequency", llmUsage);
    player.set("generalLlmUsage", generalLlmUsage);
    player.set("aiChatbotImpressions", aiChatbotImpressions);
    player.set("aiChatbotImpressionItemOrder", aiChatbotImpressionOrder);
    player.set("llmHeardOf", llmHeardOf);
    player.set("llmUsed", llmUsed);
    player.set("prePetSliderValue", prePetSliderValue);
    player.set("prePetCatsOnRight", catsOnRight);
    player.set(
      "prePetCatsPreference",
      catsOnRight ? prePetSliderValue : 100 - prePetSliderValue
    );
    next();
  }

  function handleContinueWithoutInteraction() {
    if (surveyPage < 5) {
      setSurveyPage((page) => page + 1);
      return;
    }

    next();
  }

  function renderBypassButton() {
    if (!testMode) {
      return null;
    }

    return (
      <div data-no-interaction>
        <Button type="button" primary handleClick={handleContinueWithoutInteraction}>
          Bypass page (test)
        </Button>
      </div>
    );
  }

  return (
    <div
      ref={pageTopRef}
      className={`py-8 mx-auto px-8 space-y-6 ${
        surveyPage === 3 ? "max-w-[1070px]" : "max-w-3xl"
      }`}
    >
      {testMode ? (
        <div className="flex justify-end" data-no-interaction>
          <Button type="button" primary handleClick={() => next()}>
            Skip entire intro survey (test)
          </Button>
        </div>
      ) : null}
      {surveyPage === 1 ? (
        <form className="space-y-6" onSubmit={(event) => event.preventDefault()}>
          <div className="rounded-md border border-gray-200 p-5 space-y-4">
            {/* <h3 className="text-xl font-semibold text-gray-900">Demographics</h3> */}

            <div>
              <label className={labelClassName}>Age</label>
              <input
                className={selectionInputClassName}
                type="number"
                min={0}
                value={age}
                onChange={(e) => setAge(e.target.value)}
              />
            </div>

            <div>
              <label className={labelClassName}>Education</label>
              <select
                className={selectionInputClassName}
                value={education}
                onChange={(e) => setEducation(e.target.value)}
              >
                <option value="">Select education level</option>
                <option value="Less than high school">Less than high school</option>
                <option value="High school">High school</option>
                <option value="Some college">Some college</option>
                <option value="Associate degree">Associate degree</option>
                <option value="Four-year degree">Four-year degree</option>
                <option value="Master's degree">Master's degree</option>
                <option value="Professional degree (PhD, JD, MD, etc.)">
                  Professional degree (PhD, JD, MD, etc.)
                </option>
              </select>
            </div>

            <div>
              <label className={labelClassName}>Gender</label>
              <select
                className={selectionInputClassName}
                value={gender}
                onChange={(e) => setGender(e.target.value)}
              >
                <option value="">Select gender</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div>
              <label className={labelClassName}>
                Are you of Hispanic, Latino, or Spanish origin?
              </label>
              <select
                className={selectionInputClassName}
                value={hispanicLatinoOrigin}
                onChange={(e) => setHispanicLatinoOrigin(e.target.value)}
              >
                <option value="">Select one</option>
                <option value="No">No</option>
                <option value="Yes">Yes</option>
              </select>
            </div>

            <div>
              <label className={labelClassName}>What is your race? (Select all that apply)</label>
              <div className="space-y-2">
                {censusRaceOptions.map((race) => (
                  <label key={race} className="flex items-center gap-2 text-lg text-gray-700">
                    <input
                      type="checkbox"
                      checked={raceSelections.includes(race)}
                      onChange={(e) => handleRaceSelectionChange(race, e.target.checked)}
                    />
                    <span>{race}</span>
                  </label>
                ))}
              </div>
            </div>

          </div>

          <div className="flex items-center justify-end gap-3">
            {renderBypassButton()}
            <Button handleClick={() => setSurveyPage(2)} disabled={!testMode && !hasDemographics}>
              Continue
            </Button>
          </div>
        </form>
      ) : surveyPage === 2 ? (
        <form className="space-y-6" onSubmit={(event) => event.preventDefault()}>
          <div className="rounded-md border border-gray-200 p-5 space-y-5">
            <div className="space-y-2">
              <label className={surveyQuestionClassName}>
                How often do you use AI chatbots?
              </label>
              <p className="text-xl text-gray-700">
                AI chatbots include platforms like ChatGPT, Claude, Gemini, Grok, Kimi, etc.
              </p>
              <div className="space-y-2">
                {llmUsageFrequencyOptions.map((option) => (
                  <label
                    key={`general-llm-${option.value}`}
                    className={surveyOptionClassName}
                  >
                    <input
                      type="radio"
                      name="general-llm-usage"
                      value={option.value}
                      checked={generalLlmUsage === option.value}
                      onChange={(e) => setGeneralLlmUsage(e.target.value)}
                    />
                    <span>{option.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <p className={surveyQuestionClassName}>
              How often do you use AI chatbots for the following, if at all:
            </p>

            {llmUsageCategories.map((cat) => (
              <div key={cat.key} className="space-y-2">
                <label className="block text-xl font-semibold text-gray-800">{cat.label}</label>
                <div className="space-y-2">
                  {llmUsageFrequencyOptions.map((option) => (
                    <label
                      key={`${cat.key}-${option.value}`}
                      className={surveyOptionClassName}
                    >
                      <input
                        type="radio"
                        name={`llm-usage-${cat.key}`}
                        value={option.value}
                        checked={(llmUsage[cat.key] || "") === option.value}
                        onChange={(e) => handleLlmUsageChange(cat.key, e.target.value)}
                      />
                      <span>{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between">
            <Button handleClick={() => setSurveyPage(1)} primary>
              Back
            </Button>
            <div className="flex items-center gap-3">
              {renderBypassButton()}
              <Button handleClick={() => setSurveyPage(3)} disabled={!hasLlmUsage}>
                Continue
              </Button>
            </div>
          </div>
        </form>
      ) : surveyPage === 3 ? (
        <form className="space-y-6" onSubmit={(event) => event.preventDefault()}>
          <div className="rounded-md border border-gray-200 p-5 space-y-6">
            <p className={surveyQuestionClassName}>
              Based on your interaction with / impression of <strong>AI chatbots</strong>, please
              indicate how much you agree that <strong>AI chatbots</strong> are each of the
              following.
            </p>
            <p className="text-center text-xl text-gray-700">
              AI chatbots include platforms like ChatGPT, Claude, Gemini, Grok, Kimi, etc.
            </p>

            <div className="overflow-x-auto">
              <div className="min-w-[760px] space-y-3">
                <div className="grid grid-cols-[180px_repeat(7,minmax(0,1fr))] items-end gap-x-0">
                  <div />
                  {aiChatbotLikertOptions.map((option) => (
                    <div
                      key={`ai-chatbot-impression-heading-${option.value}`}
                      className="text-center text-lg font-semibold leading-tight whitespace-pre-line text-gray-700"
                    >
                      {option.label}
                    </div>
                  ))}
                </div>

                {aiChatbotImpressionOrder.map((itemKey) => {
                  const item = aiChatbotImpressionItems.find(({ key }) => key === itemKey);

                  return (
                    <div
                      key={item.key}
                      className="grid grid-cols-[180px_repeat(7,minmax(0,1fr))] items-center gap-x-0 border-t border-gray-200 pt-3"
                    >
                      <div className="text-xl font-semibold text-gray-800">
                        {capitalizeLabel(item.label)}
                      </div>
                      {aiChatbotLikertOptions.map((option) => (
                        <label
                          key={`${item.key}-${option.value}`}
                          className="flex min-h-[2.25rem] items-center justify-center"
                          aria-label={`${item.label}: ${option.label.replace(/\n/g, " ")}`}
                        >
                          <input
                            type="radio"
                            name={`ai-chatbot-impression-${item.key}`}
                            value={option.value}
                            checked={aiChatbotImpressions[item.key] === option.value}
                            className="h-6 w-6"
                            onChange={(e) =>
                              handleAiChatbotImpressionChange(item.key, e.target.value)
                            }
                          />
                        </label>
                      ))}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <Button handleClick={() => setSurveyPage(2)} primary>
              Back
            </Button>
            <div className="flex items-center gap-3">
              {renderBypassButton()}
              <Button handleClick={() => setSurveyPage(4)} disabled={!hasAiChatbotImpressions}>
                Continue
              </Button>
            </div>
          </div>
        </form>
      ) : surveyPage === 4 ? (
        <form className="space-y-6" onSubmit={(event) => event.preventDefault()}>
          <div className="rounded-md border border-gray-200 p-5 space-y-5">
            <div className="space-y-2">
              <label className={surveyQuestionClassName}>Which of these AI chatbots have you heard of?</label>
              <div className="space-y-2">
                {llmOptions.map((llm) => (
                  <label key={`heard-${llm}`} className={surveyOptionClassName}>
                    <input
                      type="checkbox"
                      checked={llmHeardOf.includes(llm)}
                      onChange={(e) =>
                        setLlmHeardOf((prev) =>
                          e.target.checked ? [...prev, llm] : prev.filter((x) => x !== llm)
                        )
                      }
                    />
                    <span>{llm}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <label className={surveyQuestionClassName}>Which of these AI chatbots do you use, if any?</label>
              <div className="space-y-2">
                {llmOptions.map((llm) => (
                  <label key={`used-${llm}`} className={surveyOptionClassName}>
                    <input
                      type="checkbox"
                      checked={llmUsed.includes(llm)}
                      onChange={(e) =>
                        setLlmUsed((prev) =>
                          e.target.checked ? [...prev, llm] : prev.filter((x) => x !== llm)
                        )
                      }
                    />
                    <span>{llm}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <Button handleClick={() => setSurveyPage(3)} primary>
              Back
            </Button>
            <div className="flex items-center gap-3">
              {renderBypassButton()}
              <Button handleClick={() => setSurveyPage(5)}>
                Continue
              </Button>
            </div>
          </div>
        </form>
      ) : (
        <form className="space-y-6" onSubmit={handleSubmit}>
          <div className="rounded-md border border-gray-200 p-5 space-y-5">
            <h3 className="text-2xl font-semibold text-gray-900">Pet preference</h3>

            <div>
              <label className={labelClassName}>
                Which make better pets: {leftPetLabel.toLowerCase()} or {rightPetLabel.toLowerCase()}?
              </label>
              <div className="flex items-center space-x-3">
                <span className="w-12 text-right text-2xl font-medium text-gray-700">
                  {leftPetLabel}
                </span>
                <input
                  type="range"
                  min={0}
                  max={100}
                  value={prePetSliderValue}
                  onChange={handlePrePetSliderChange}
                  className="flex-1 neutral-slider"
                />
                <span className="w-12 text-2xl font-medium text-gray-700">
                  {rightPetLabel}
                </span>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <Button handleClick={() => setSurveyPage(4)} primary>
              Back
            </Button>
            <div className="flex items-center gap-3">
              {renderBypassButton()}
              <Button type="submit" disabled={!prePetSliderTouched}>
                Continue
              </Button>
            </div>
          </div>
        </form>
      )}
    </div>
  );
}
